import React from 'react';
import { Flex, Div, Gutter, Title, BodyText, GridBag } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { AdminSpecItem, LocaleResponse } from '@cdaas/api-clients/dist/product-service-openapi';

import { SpecItemWithKey, SpecItemTranslationWithKey } from '../../../../utils/types';
import { getInitialSpec } from '../../../../utils/products';
import { removeEntityByIndex, updateEntityByIndex } from '../../../../utils/entities';
import { Errors } from '../utils';
import Spec from './Spec';

interface SpecsProps {
  title: string;
  specs: AdminSpecItem[] | undefined;
  currentLocale: LocaleResponse;
  locales: LocaleResponse[];
  handleSpecsChange: (specs: AdminSpecItem[]) => void;
  specsErrors: Errors['specs'];
}

function Specs(props: SpecsProps) {
  const { title, specs, currentLocale, locales, handleSpecsChange, specsErrors } = props;
  const { t } = useTranslation('productscreen');

  const handleMainSpecAddition = () => {
    const spec = getInitialSpec(locales.map(locale => locale.id));
    handleSpecsChange([...(specs || []), spec]);
  };

  const handleSpecUpdate = (updatedSpec: SpecItemWithKey) => {
    const index = (specs || []).findIndex((spec: SpecItemWithKey) => {
      return (spec.id && spec.id === updatedSpec.id) || (spec.key && spec.key === updatedSpec.key);
    });
    if (index !== -1) {
      const updatedSpecs = updateEntityByIndex(specs, index, updatedSpec);
      handleSpecsChange(updatedSpecs);
    }
  };

  const handleSpecTranslationUpdate = (spec: SpecItemWithKey, updatedTranslation: SpecItemTranslationWithKey) => {
    const index = (spec.translations || []).findIndex((translation: SpecItemTranslationWithKey) => {
      return (translation.id && translation.id === updatedTranslation.id) || (translation.key && translation.key === updatedTranslation.key);
    });
    let updatedSpecTranslations: AdminSpecItem['translations'];
    if (index === -1) {
      updatedSpecTranslations = [...(spec.translations || []), updatedTranslation];
    } else {
      updatedSpecTranslations = updateEntityByIndex(spec.translations, index, updatedTranslation);
    }
    handleSpecUpdate({ ...spec, translations: updatedSpecTranslations });
  };

  const handleSpecRemoval = (specToRemove: SpecItemWithKey) => {
    if (specs) {
      const index = specs.findIndex((spec: SpecItemWithKey) => {
        return (spec.id && spec.id === specToRemove.id) || (spec.key && spec.key === specToRemove.key);
      });
      if (index !== -1) {
        const updatedSpecs = removeEntityByIndex(specs, index);
        handleSpecsChange(updatedSpecs);
      }
    }
  };

  return (
    <Flex directionColumn>
      <Title size="md">{title}</Title>
      <Gutter />
      <BodyText color="gray400">{t('specs_section.info')}</BodyText>
      <Gutter />
      {(specs || []).map((spec: SpecItemWithKey, index) => (
        <GridBag key={index}>
          <GridBag.Item md={10}>
            <Spec
              index={index}
              locale={currentLocale}
              spec={spec}
              handleSpecUpdate={handleSpecUpdate}
              handleSpecTranslationUpdate={handleSpecTranslationUpdate}
              handleSpecRemoval={handleSpecRemoval}
              errors={specsErrors?.find(specsError => specsError.index === index)}
            />
          </GridBag.Item>
        </GridBag>
      ))}
      <Div>
        <BodyText clickableAppearance={true} color="purple500" onClick={() => handleMainSpecAddition()}>
          {`+ ${t('specs_section.add_spec')}`}
        </BodyText>
      </Div>
    </Flex>
  );
}

export default React.memo(Specs);
